import React from 'react'
import { lazyWithRetry } from 'utils/lazyWithRetry'
import PropTypes from 'prop-types'
import { path } from 'ramda'
import { Route, Switch, Redirect } from 'react-router'
import * as ROUTES from 'constants/routes'
import {
  LOCAL,
  DEV_INTERNAL,
  DEV_EXTERNAL,
  DEV_INTERNAL_AKS,
  DEV_INTERNAL_VIT,
  QA_INTERNAL,
  QA_EXTERNAL,
  QA_INTERNAL_AKS,
  QA_EXTERNAL_AKS,
  QA_INTERNAL_VIT,
  QA_EXTERNAL_VIT,
} from 'utils/constants'
import ReduxDrawer from 'components/patterns/Drawer/ReduxDrawer/ReduxDrawer'
import RulesUtils from 'components/RulesUtils'
import { HelpModalProvider } from 'components/HelpModal'
import { AppContainer, AppMain } from '../../App.style'
import NavSticky from '../NavSticky'

const TruckCompareContainer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "TruckCompareContainer" */ 'components/pages/TruckCompareContainer'
  )
)

const VehicleSearch = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "VehicleSearch" */ 'components/pages/VehicleSearch'
  )
)

const ReferenceParameterSearch = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "ReferenceParameterSearch" */ 'components/pages/ReferenceParameterSearch'
  )
)
const ReferenceParameterSchematic = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "ReferenceParameterSchematic" */ 'components/pages/ReferenceParameterSchematic'
  )
)
const SupportParameterSearch = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "SupportParameterSearch" */ 'components/pages/SupportParameterSearch/SupportParameterSearch'
  )
)
const SupportParameterSchematic = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "SupportParameterSchematic" */ 'components/pages/SupportParameterSchematic'
  )
)
const ChecFeaturesContainer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "ChecFeaturesContainer" */ 'components/pages/ChecFeaturesContainer'
  )
)
const FeatureSearch = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "FeatureSearch" */ 'components/pages/FeatureSearch'
  )
)
const FeatureCollections = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "FeatureCollections" */ 'components/pages/FeatureCollections'
  )
)
const FeatureCollectionDetails = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "FeatureCollectionDetails" */ 'components/pages/FeatureCollectionDetails'
  )
)
const FeatureSchematicContainer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "FeatureSchematicContainer" */ 'components/pages/FeatureSchematic'
  )
)
const FastFeatures = lazyWithRetry(() =>
  import(/* webpackChunkName: "FastFeatures" */ 'components/pages/FastFeatures')
)
const SystemRequirementsContainer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "SystemRequirementsContainer" */ 'components/pages/SystemRequirementsContainer'
  )
)
const CTPSearchContainer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "CTPSearchContainer" */ 'components/pages/CTPSearchContainer'
  )
)
const CircuitDetailsContainer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "CircuitDetailsContainer" */ 'components/pages/CircuitDetailsContainer'
  )
)
const BundleDetailsContainer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "BundleDetailsContainer" */ 'components/pages/BundleDetailsContainer'
  )
)
const Home = lazyWithRetry(() =>
  import(/* webpackChunkName: "Home" */ 'components/pages/Home')
)
const ZoneAlertContainer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "ZoneAlertContainer" */ 'components/pages/ZoneAlert'
  )
)
const NavPrimary = lazyWithRetry(() =>
  import(/* webpackChunkName: "NavPrimary" */ 'components/NavPrimary')
)
const PageMetaContainer = lazyWithRetry(() =>
  import(/* webpackChunkName: "PageMetaContainer" */ 'components/PageMeta')
)
const PageMetaNotifications = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "PageMetaNotifications" */ 'components/PageMeta/PageMetaNotificationsContainer'
  )
)
const FooterContainer = lazyWithRetry(() =>
  import(/* webpackChunkName: "FooterContainer" */ 'components/Footer')
)
const NavSecondary = lazyWithRetry(() =>
  import(/* webpackChunkName: "NavSecondary" */ 'components/NavSecondary')
)
const Vehicle = lazyWithRetry(() =>
  import(/* webpackChunkName: "Vehicle" */ 'components/pages/Vehicle')
)
const ReleaseNotes = lazyWithRetry(() =>
  import(/* webpackChunkName: "ReleaseNotes" */ 'components/pages/ReleaseNotes')
)
const DashLayout = lazyWithRetry(() =>
  import(/* webpackChunkName: "DashLayout" */ 'components/pages/DashLayout')
)
const DashView = lazyWithRetry(() =>
  import(/* webpackChunkName: "DashView" */ 'components/DashBuilder/DashView')
)
const ODXDataTranslator = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "ODXDataTranslator" */ 'components/ODXDataTranslator'
  )
)
const LineNoteReader = lazyWithRetry(() =>
  import(/* webpackChunkName: "LineNoteReader" */ 'components/LineNoteReader')
)
const FastFeatureErrors = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "FastFeatureErrors" */ 'components/FastFeatureErrors'
  )
)
const PartsViewer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "PartsViewer" */ 'components/SwitchAndGauge/PartsViewer'
  )
)
const TemEnablement = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "TemEnablement" */ 'components/pages/TemEnablement'
  )
)
const ErrorTemplates = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "ErrorTemplates" */ 'components/ErrorBoundary/ErrorTemplates'
  )
)
const PtoTemplateSearch = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "PtoTemplateSearch" */ 'components/pages/PtoTemplates/Search'
  )
)
const PtoTemplate = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "PtoTemplate" */ 'components/pages/PtoTemplates/Template'
  )
)
const RolesEditor = lazyWithRetry(() =>
  import(/* webpackChunkName: "RoleEditor" */ 'components/RoleEditor')
)

const hostname = path(['location', 'hostname'], window)

const isDevelopment = () =>
  [
    LOCAL,
    DEV_INTERNAL,
    DEV_EXTERNAL,
    DEV_INTERNAL_AKS,
    DEV_INTERNAL_VIT,
    QA_INTERNAL,
    QA_EXTERNAL,
    QA_INTERNAL_AKS,
    QA_EXTERNAL_AKS,
    QA_INTERNAL_VIT,
    QA_EXTERNAL_VIT,
  ].includes(hostname)

const Routes = () => (
  <AppContainer className="app-container">
    <Switch>
      <Route
        exact
        path={ROUTES.dashLayout}
        component={(props) => (
          <>
            <ReduxDrawer {...props} />
            <DashLayout {...props} />
          </>
        )}
      />
      <Route
        exact
        path={ROUTES.dashBuilderId}
        render={(props) => (
          <>
            <ReduxDrawer {...props} />
            <DashView {...props} />
          </>
        )}
      />
      <Route
        exact
        path={ROUTES.odxDataTranslator}
        component={ODXDataTranslator}
      />
      <NotProductionRoute exact path={ROUTES.rules} component={RulesUtils} />
      <Route exact path={ROUTES.odxModule} component={ODXDataTranslator} />
      <Route path={ROUTES.odxVariant} component={ODXDataTranslator} />
      <Route exact path={ROUTES.lineNoteReader} component={LineNoteReader} />
      <Route
        exact
        path={ROUTES.fastFeatureErrors}
        component={FastFeatureErrors}
      />
      <Route exact path={ROUTES.parts} component={PartsViewer} />
      <Route exact path={ROUTES.partsId} component={PartsViewer} />
      <Route>
        <Route path="/" component={NavPrimary} />
        <NavSticky>
          <Route path={ROUTES.vehiclesId} component={NavSecondary} />
          <Route path="/" component={PageMetaNotifications} />
        </NavSticky>
        {/* Issue 3 (#496741): Portal target for page-level banners that must
            render above the breadcrumb (PageMetaContainer). Components use
            ReactDOM.createPortal to mount here without breaking component tree. */}
        <div id="page-banner-portal" />
        <Route path="/" component={PageMetaContainer} />
        <HelpModalProvider>
          <Route path="/" component={ReduxDrawer} />
          <Switch>
            <RouteContainer
              path={ROUTES.temEnablement}
              component={TemEnablement}
            />
            <RouteContainer
              path={ROUTES.releaseNotes}
              component={ReleaseNotes}
            />
            <RouteContainer
              path={ROUTES.featuresId}
              component={FeatureSchematicContainer}
            />
            <RouteContainer
              path={ROUTES.ptoTemplatesId}
              component={PtoTemplate}
            />
            <RouteContainer
              path={ROUTES.ptoTemplates}
              component={PtoTemplateSearch}
            />
            <RouteContainer path="/fast-features" component={FastFeatures} />
            <RouteContainer path={ROUTES.features} component={FeatureSearch} />
            <RouteContainer path={ROUTES.features} component={FeatureSearch} />
            <RouteContainer
              path={ROUTES.featureCollectionsId}
              component={FeatureCollectionDetails}
            />
            <RouteContainer
              path={ROUTES.featureCollections}
              component={FeatureCollections}
            />
            <RouteContainer
              path={ROUTES.vehiclesCompare}
              component={TruckCompareContainer}
            />
            <RouteContainer path={ROUTES.vehiclesId} component={Vehicle} />
            <RouteContainer path={ROUTES.vehicles} component={VehicleSearch} />
            <RouteContainer
              path={ROUTES.referenceParametersRPNumRelease}
              component={ReferenceParameterSchematic}
            />
            <RouteContainer
              path={ROUTES.referenceParameters}
              component={ReferenceParameterSearch}
            />
            <RouteContainer
              path={ROUTES.supportParametersSPNum}
              component={SupportParameterSchematic}
            />
            <RouteContainer
              path={ROUTES.supportParameters}
              component={SupportParameterSearch}
            />
            <RouteContainer
              path={ROUTES.checFeatures}
              component={ChecFeaturesContainer}
            />
            <RouteContainer
              path={ROUTES.alerts}
              component={ZoneAlertContainer}
            />
            <RouteContainer
              path={ROUTES.circuitsId}
              component={CircuitDetailsContainer}
            />
            <RouteContainer
              path={ROUTES.bundlesId}
              component={BundleDetailsContainer}
            />
            <RouteContainer path={ROUTES.ctp} component={CTPSearchContainer} />
            <RouteContainer
              path={ROUTES.requirements}
              component={SystemRequirementsContainer}
            />
            <NotProductionRoute
              exact
              path={ROUTES.errors}
              component={ErrorTemplates}
            />
            <NotProductionRoute
              exact
              path={ROUTES.rolesEditor}
              component={RolesEditor}
            />
            <RouteContainer path="/" component={Home} />
            <Redirect to="/" />
          </Switch>
        </HelpModalProvider>
        <Route path="/" component={FooterContainer} />
      </Route>
    </Switch>
  </AppContainer>
)

export default Routes

const RouteContainer = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={(routerProps) => (
      <AppMain className="app-main" routerProps={routerProps}>
        <Component {...routerProps} />
      </AppMain>
    )}
  />
)

RouteContainer.propTypes = {
  component: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
}

const NotProductionRoute = ({ component: Component, ...rest }) => (
  <>
    {isDevelopment() ? (
      <Route
        {...rest}
        render={(routerProps) => <Component {...routerProps} />}
      />
    ) : (
      <Redirect to="/" />
    )}
  </>
)

NotProductionRoute.propTypes = {
  component: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
}
